import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  TextField,
  Grid,
  Paper,
  Chip
} from '@mui/material';
import {
  Phone as PhoneIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  MonetizationOn as MoneyIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { confirmCustomerApproval } from '../../services/maintenanceCostService';
import { formatCurrency } from '../../utils/numberUtils';
import { MaintenanceRecord } from '../../utils/types';

interface CustomerApprovalDialogProps {
  open: boolean;
  onClose: () => void;
  maintenance: MaintenanceRecord;
  userId: string;
  onSuccess: () => void;
}

const CustomerApprovalDialog: React.FC<CustomerApprovalDialogProps> = ({
  open,
  onClose,
  maintenance,
  userId,
  onSuccess
}) => {
  const [approvalStatus, setApprovalStatus] = useState<'approved' | 'rejected' | ''>('');
  const [finalCost, setFinalCost] = useState<number>(maintenance.estimatedCost || maintenance.initialCost || 0);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!approvalStatus) {
      setError('يجب اختيار حالة الموافقة');
      return;
    }

    if (approvalStatus === 'approved' && finalCost <= 0) {
      setError('يجب إدخال تكلفة نهائية صحيحة');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await confirmCustomerApproval(
        maintenance.id,
        approvalStatus === 'approved',
        approvalStatus === 'approved' ? finalCost : undefined,
        userId
      );

      onSuccess();
      onClose();
      
      // إعادة تعيين القيم
      setApprovalStatus('');
      setNotes('');
      setError(null);
    } catch (error) {
      console.error('Error confirming customer approval:', error);
      setError('حدث خطأ أثناء تسجيل موافقة العميل');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setApprovalStatus('');
      setNotes('');
      setError(null);
    }
  };

  const estimatedCost = maintenance.estimatedCost || maintenance.initialCost || 0;
  const costDifference = finalCost - estimatedCost;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PhoneIcon color="primary" />
          <Typography variant="h6">
            تأكيد موافقة العميل على التكلفة
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box py={1}>
          {/* معلومات الطلب */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              معلومات الطلب
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>العميل:</strong> {maintenance.customerName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>نوع الجهاز:</strong> {maintenance.deviceType}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>التكلفة المبدئية:</strong> {formatCurrency(maintenance.initialCost || 0)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>التكلفة المقدرة:</strong> {formatCurrency(estimatedCost)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* تحذير الانتظار */}
          <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
            <Typography variant="body2">
              <strong>الصيانة متوقفة حالياً:</strong> العميل طلب الاتصال به للموافقة على التكلفة المقدرة قبل بدء الصيانة.
            </Typography>
          </Alert>

          {/* اختيار حالة الموافقة */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">
              <Typography variant="subtitle1" fontWeight="medium">
                نتيجة الاتصال بالعميل
              </Typography>
            </FormLabel>
            <RadioGroup
              value={approvalStatus}
              onChange={(e) => {
                setApprovalStatus(e.target.value as 'approved' | 'rejected');
                setError(null);
              }}
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                value="approved"
                control={<Radio />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <ApproveIcon color="success" fontSize="small" />
                    <Typography>وافق العميل على التكلفة المقدرة</Typography>
                  </Box>
                }
                disabled={loading}
              />
              <FormControlLabel
                value="rejected"
                control={<Radio />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <RejectIcon color="error" fontSize="small" />
                    <Typography>رفض العميل التكلفة أو ألغى الطلب</Typography>
                  </Box>
                }
                disabled={loading}
              />
            </RadioGroup>
          </FormControl>

          {/* إدخال التكلفة النهائية إذا وافق العميل */}
          {approvalStatus === 'approved' && (
            <Box mb={3}>
              <TextField
                label="التكلفة النهائية المتفق عليها"
                type="number"
                fullWidth
                value={finalCost || ''}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  setFinalCost(Math.max(0, value));
                  setError(null);
                }}
                InputProps={{
                  startAdornment: (
                    <MoneyIcon color="primary" sx={{ mr: 1 }} />
                  ),
                  endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>جنيه</Typography>,
                  inputProps: { min: 0, step: 0.01 }
                }}
                helperText="التكلفة النهائية التي وافق عليها العميل"
                disabled={loading}
                sx={{ mb: 2 }}
              />

              {/* عرض الفرق في التكلفة */}
              {costDifference !== 0 && (
                <Box mb={2}>
                  <Chip
                    label={`${costDifference > 0 ? 'زيادة' : 'نقص'}: ${formatCurrency(Math.abs(costDifference))}`}
                    color={costDifference > 0 ? 'warning' : 'success'}
                    size="small"
                  />
                </Box>
              )}

              {/* ملخص التكلفة النهائية */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="subtitle2" color="success.dark" gutterBottom>
                  ملخص التكلفة المتفق عليها
                </Typography>
                <Typography variant="body2" color="success.dark">
                  <strong>التكلفة النهائية:</strong> {formatCurrency(finalCost)}
                </Typography>
                {costDifference !== 0 && (
                  <Typography variant="caption" color="success.dark" display="block" sx={{ mt: 0.5 }}>
                    {costDifference > 0 ? 'زيادة' : 'نقص'} عن التكلفة المقدرة: {formatCurrency(Math.abs(costDifference))}
                  </Typography>
                )}
              </Paper>
            </Box>
          )}

          {/* رسالة الرفض */}
          {approvalStatus === 'rejected' && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>تم رفض التكلفة:</strong> سيتم إيقاف الصيانة وإعادة الجهاز للعميل.
              </Typography>
            </Alert>
          )}

          {/* ملاحظات */}
          <TextField
            label="ملاحظات المكالمة"
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="أضف ملاحظات حول المكالمة مع العميل..."
            disabled={loading}
            sx={{ mb: 2 }}
          />

          {/* رسالة خطأ */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* معلومات إضافية */}
          <Alert severity="info">
            <Typography variant="body2">
              <strong>ملاحظة:</strong> بعد تأكيد الموافقة، سيتم استئناف الصيانة تلقائياً. 
              في حالة الرفض، سيتم تغيير حالة الطلب إلى "ملغي".
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !approvalStatus}
          startIcon={loading ? <CircularProgress size={20} /> : 
            approvalStatus === 'approved' ? <ApproveIcon /> : <RejectIcon />}
          color={approvalStatus === 'approved' ? 'success' : 'error'}
        >
          {loading ? 'جاري الحفظ...' : 
           approvalStatus === 'approved' ? 'تأكيد الموافقة' : 'تأكيد الرفض'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerApprovalDialog;
